import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ESignService } from 'src/app/services/e-sign.service';
import { CatUploadService } from 'src/app/services/cat-upload.service';
import { MessageService } from 'primeng/api';

@Component({
    selector: 'app-e-sign-retrigger',
    templateUrl: './e-sign-retrigger.component.html',
    styleUrls: ['./e-sign-retrigger.component.scss'],
    standalone: false
})
export class ESignRetriggerComponent implements OnInit {
  retriggerTransferState: any[] = [];
  referenceId = '';
  orderDetails: any[] = [];
  signerDetails: any[] = [];
  signerForm: FormGroup;
  signMethods: string[] = [];
  signPlacements: string[] = [];
  contractRelations: string[] = [];
  legalConstitutions: string[] = [];
  officialDocumentTypes: string[] = [];
  partyTypes: string[] = [];
  isSubmitting = false;

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private eSignService: ESignService,
    private catUploadService: CatUploadService,
    private messageService: MessageService,
  ) { }

  ngOnInit(): void {
    const transferState = history.state?.retriggerTransferState;
    this.signerDetails = transferState?.signerDetails || [];
    this.orderDetails = transferState?.orderDetail || [];
    if (!this.signerDetails.length || !this.orderDetails.length) {
      this.initializeEmptyForm();
      this.messageService.add({
        severity: 'warn',
        detail: 'No eligible NeSL signers were supplied for reinitiate',
      });
      this.router.navigate(['/doqfy/orders/orders-list']);
      return;
    }
    this.catUploadService.getOrValidateReferenceId().subscribe(
      (res: any) => {
        if (res.status === 200 && res.content?.reference_id) {
          this.referenceId = res.content.reference_id;
        }
      },
      () => {
        this.referenceId = '';
        this.messageService.add({
          severity: 'error',
          detail: 'Unable to generate a NeSL reinitiate reference',
        });
      }
    );

    if (this.signerDetails.length) {
      this.extractDropdownValues();
      this.createForm();
    } else {
      this.initializeEmptyForm();
    }
  }


    
  extractDropdownValues(): void {
    this.signMethods = this.getUniqueValues('sign_method');
    this.signPlacements = this.getUniqueValues('sign_position');
    this.contractRelations = this.getUniqueValues('CONTRACT_RELATION');
    this.legalConstitutions = this.getUniqueValues('CONSTITUTION');
    this.officialDocumentTypes = this.getUniqueValues('Official_Document_Type');
    this.partyTypes = this.getUniqueValues('PARTY_TYPE');
  }

  getUniqueValues(key: string): string[] {
    return [...new Set(this.signerDetails.map(s => s[key]).filter(Boolean))];
  }

  onChange(index: number): void {
    const signerGroup = this.signers.at(index) as FormGroup;
    this.signerDetails[index] = { ...this.signerDetails[index], ...signerGroup.value };
  }

  retrigger(): void {
    if (this.isSubmitting) return;
    this.signerForm.markAllAsTouched();
    const order = this.orderDetails[0];
    const orderId = Number(order?.order?.id);
    const accountId = Number(order?.account?.id);
    const branchId = Number(order?.order?.branch);
    const partyUsers = this.mapSignerDetails();
    if (
      this.signerForm.invalid ||
      !Number.isInteger(orderId) ||
      !Number.isInteger(accountId) ||
      !Number.isInteger(branchId) ||
      !this.referenceId ||
      partyUsers.length !== this.signerDetails.length
    ) {
      this.messageService.add({
        severity: 'error',
        detail: 'Enter a valid name and DOI/DOB for every eligible signer',
      });
      return;
    }

    const retriggerPayload = {
      order_details: [{
        order_id: orderId,
        account_id: accountId,
        branch_id: branchId,
        retrigger: true,
        referance_id: this.referenceId,
        esigns: { party_users: partyUsers }
      }]
    };

    this.isSubmitting = true;
    this.eSignService.retriggerNeslOrder(retriggerPayload).subscribe({
      next: (response: any) => {
        this.isSubmitting = false;
        const content = response?.content;
        if (
          content?.order_id !== orderId ||
          content?.reference_id !== this.referenceId ||
          !content?.order_version_id
        ) {
          this.messageService.add({
            severity: 'error',
            detail: 'NeSL returned an invalid reinitiate response',
          });
          return;
        }
        this.messageService.add({
          severity: 'success',
          detail: response?.message || 'NeSL signers reinitiated successfully',
        });
        this.router.navigate(['/doqfy/orders/orders-list']);
      },
      error: (error) => {
        this.isSubmitting = false;
        this.messageService.add({
          severity: 'error',
          detail:
            error?.error?.message ||
            'Unable to reinitiate the selected NeSL signers',
        });
      },
    });
  }

  mapSignerDetails(): any[] {
    return this.signers.controls.map((control: FormGroup) => {
      const signer = control.getRawValue();
      return {
        name: String(signer.name || '').trim(),
        DOI: signer.DOI,
        signatory_id: signer.signatory_id,
        signatory_sequence: Number(signer.signatory_sequence),
      };
    });
  }

  createForm(): void {
    this.signerForm = this.fb.group({
      signers: this.fb.array(this.signerDetails.map(signer => this.createSignerGroup(signer)))
    });
  }

  initializeEmptyForm(): void {
    this.signerForm = this.fb.group({ signers: this.fb.array([]) });
  }

  createSignerGroup(signer: any): FormGroup {
    return this.fb.group({
      name: [
        signer.name || '',
        [
          Validators.required,
          Validators.maxLength(255),
          Validators.pattern(/^[^<>]+$/),
        ],
      ],
      email: [{ value: signer.email || '', disabled: true }, [Validators.required, Validators.email]],
      contact_number: [{ value: this.maskContactNumber(signer.contact_number), disabled: true }, [Validators.required, Validators.pattern(/^\d{10}$/)]],
      sign_position: [{ value: signer.sign_position || '', disabled: true }, Validators.required],
      sign_method: [{ value: signer.sign_method || '', disabled: true }, Validators.required],
      status: [{ value: signer.status || '', disabled: true }, Validators.required],
      signatory_id: [{ value: signer.signatory_id || '', disabled: true }, Validators.required],
      signatory_sequence: [{ value: signer.signatory_sequence || '', disabled: true }, Validators.required],
      position_details: this.fb.array(signer.position_details?.map(pos => this.fb.control({ value: pos, disabled: true })) || []),
      esign_otp: [{ value: signer.esign_otp || '', disabled: true }],
      emudhra_gateway: this.fb.group({ NeSL: [{ value: signer.emudhra_gateway?.NeSL || false, disabled: true }] }),
      esign_webcam: [{ value: signer.esign_webcam || false, disabled: true }],
      sign_provider: [{ value: signer.sign_provider || '', disabled: true }, Validators.required],
      signed_at: [{ value: signer.signed_at || '', disabled: true }, Validators.required],
      esign_id: [{ value: signer.esign_id || '', disabled: true }, Validators.required],
      DOI: [signer.DOI || '', Validators.required],
      PARTY_TYPE: [{ value: signer.PARTY_TYPE || '', disabled: true }, Validators.required],
      CONSTITUTION: [{ value: signer.CONSTITUTION || '', disabled: true }, Validators.required],
      CONTRACT_RELATION: [{ value: signer.CONTRACT_RELATION || '', disabled: true }, Validators.required],
      Official_Document_ID: [{ value: signer.Official_Document_ID || '', disabled: true }, Validators.required],
      Official_Document_Type: [{ value: signer.Official_Document_Type || '', disabled: true }, Validators.required]
    });
  }

  get signers(): FormArray {
    return this.signerForm.get('signers') as FormArray;
  }

  maskContactNumber(contactNumber: string): string {
    return contactNumber?.length === 10 ? `${contactNumber.slice(0, 2)}****${contactNumber.slice(-4)}` : contactNumber;
  }

  onCancel(): void {
    this.router.navigate(['/doqfy/orders/orders-list']);
  }
}
